import { Injectable } from '@angular/core';

interface StorageItem<T> {
  data: T;
  timestamp: number;
  expiresIn?: number; // em milissegundos
}

@Injectable({
  providedIn: 'root'
})
export class LocalStorageService {

  constructor() { }

  /**
   * Salva um item no localStorage com expiração opcional
   * @param key Chave do item
   * @param data Dados a serem salvos
   * @param expiresIn Tempo de expiração em milissegundos (opcional)
   */
  setItem<T>(key: string, data: T, expiresIn?: number): void {
    const item: StorageItem<T> = {
      data,
      timestamp: Date.now(),
      expiresIn
    };
    
    localStorage.setItem(key, JSON.stringify(item));
  }

  /**
   * Recupera um item do localStorage verificando se não expirou
   * @param key Chave do item
   * @returns Dados salvos ou null se expirado/não encontrado
   */
  getItem<T>(key: string): T | null {
    const itemStr = localStorage.getItem(key);
    
    if (!itemStr) {
      return null;
    }

    try {
      const item: StorageItem<T> = JSON.parse(itemStr);
      
      // Se não tem expiração definida, retorna os dados
      if (!item.expiresIn) {
        return item.data;
      }

      // Verifica se expirou
      const now = Date.now();
      const isExpired = (now - item.timestamp) > item.expiresIn;
      
      if (isExpired) {
        this.removeItem(key);
        return null;
      }

      return item.data;
    } catch (error) {
      console.error('Erro ao fazer parse do localStorage:', error);
      this.removeItem(key);
      return null;
    }
  }

  /**
   * Remove um item do localStorage
   * @param key Chave do item
   */
  removeItem(key: string): void {
    localStorage.removeItem(key);
  }

  /**
   * Verifica se um item existe e não expirou
   * @param key Chave do item
   * @returns true se existe e não expirou
   */
  hasItem(key: string): boolean {
    return this.getItem(key) !== null;
  }

  /**
   * Limpa todos os itens do localStorage
   */
  clear(): void {
    localStorage.clear();
  }

  /**
   * Remove todos os itens expirados do localStorage
   */
  cleanExpiredItems(): void {
    const keys = Object.keys(localStorage);
    
    keys.forEach(key => {
      // Tenta recuperar o item, se retornar null significa que expirou
      this.getItem(key);
    });
  }

  /**
   * Utilitário para converter tempo em milissegundos
   */
  static timeToMs = {
    seconds: (seconds: number) => seconds * 1000,
    minutes: (minutes: number) => minutes * 60 * 1000,
    hours: (hours: number) => hours * 60 * 60 * 1000,
    days: (days: number) => days * 24 * 60 * 60 * 1000
  };
}
